/**
 * Micro-camada de DOM escrita do zero (sem framework).
 * `h()` cria elementos; utilitarios de montagem e formatacao complementam.
 */

export type Child = Node | string | number | null | undefined | false;

export interface ElementProps {
  class?: string;
  className?: string;
  text?: string;
  html?: string;
  style?: Partial<CSSStyleDeclaration> | Record<string, string>;
  dataset?: Record<string, string>;
  attrs?: Record<string, string | number | boolean | null | undefined>;
  [key: string]: unknown;
}

function isFormControl(
  element: HTMLElement,
): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  );
}

function applyProps(element: HTMLElement, props: ElementProps): void {
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;

    switch (key) {
      case 'class':
      case 'className':
        element.className = String(value);
        break;
      case 'text':
        element.textContent = String(value);
        break;
      case 'html':
        element.innerHTML = String(value);
        break;
      case 'style':
        if (typeof value === 'string') {
          element.setAttribute('style', value);
        } else {
          Object.assign(element.style, value as Record<string, string>);
        }
        break;
      case 'dataset':
        for (const [dataKey, dataValue] of Object.entries(value as Record<string, string>)) {
          element.dataset[dataKey] = dataValue;
        }
        break;
      case 'attrs':
        for (const [attrKey, attrValue] of Object.entries(
          value as Record<string, string | number | boolean>,
        )) {
          element.setAttribute(attrKey, String(attrValue));
        }
        break;
      default:
        if (key.startsWith('on') && typeof value === 'function') {
          element.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
        } else if (key === 'value' && isFormControl(element)) {
          element.value = String(value);
        } else {
          element.setAttribute(key, String(value));
        }
    }
  }
}

/** Cria um elemento HTML com filhos e propriedades. */
export function h(
  tag: string,
  props: ElementProps | null = null,
  ...children: Child[]
): HTMLElement {
  const element = document.createElement(tag);
  if (props) applyProps(element, props);

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    element.append(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }

  return element;
}

export function frag(...children: Child[]): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    fragment.append(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
  return fragment;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function mount(node: Element, ...children: Child[]): void {
  clear(node);
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/* ---------- Formatacao ---------------------------------------------------- */

// Datas e horas são formatadas no fuso oficial do sistema (America/Sao_Paulo)
// e reexportadas aqui para manter os imports das telas inalterados.
export {
  formatDate,
  formatTime,
  formatDateTime,
  formatWeekday,
  formatIsoDate,
  isoToLocalInput,
  isoToLocalDate,
  localInputToIso,
  addDaysLocal,
  getTimezone,
  setTimezone,
} from './datetime.js';

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

export function formatClock(seconds: number): string {
  const negative = seconds < 0;
  const total = Math.abs(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const base = `${hours > 0 ? `${String(hours).padStart(2, '0')}:` : ''}${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return negative ? `-${base}` : base;
}

export function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(value < 10 && index > 0 ? 1 : 0)} ${units[index]}`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Evita injecao de HTML ao interpolar valores vindos da API. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
